import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, message } from 'antd';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  PlusIcon,
  SearchIcon,
  CaretIcon,
  RefreshIcon,
  EditLineIcon,
  TrashIcon,
} from '../../assets/icons';
import {
  useGetDepartmentsQuery,
  useGetDepartmentFiltersQuery,
  useDeleteDepartmentMutation,
  type GovtDepartment,
} from '../../features/departments/departmentsApi';

/* ------------------------------------------------------------------ *
 * Government Departments — listing (Build Hierarchy → Dept. Configuration).
 * Figma 1676-2781. Backed by GET /govt-department/ (server-side search,
 * filter, sort, pagination).
 * ------------------------------------------------------------------ */

/** Avatar palettes cycled per row for the initials badge. */
const AVATARS = [
  'bg-[#e0edff] text-[#2563eb]',
  'bg-[#ffe4e6] text-[#e11d48]',
  'bg-[#dcfce7] text-[#15803d]',
  'bg-[#ccfbf1] text-[#0f766e]',
  'bg-[#fef3c7] text-[#b45309]',
];

/** Two-letter badge from the first significant words of the name. */
const initialsOf = (name: string) => {
  const words = name.split(/\s+/).filter((w) => /[a-z0-9]/i.test(w));
  return (words.slice(0, 2).map((w) => w[0]).join('') || name.slice(0, 2)).toUpperCase();
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** "Apex to <lowest level>" from the department's designations. */
const spanLabel = (dept: GovtDepartment) => {
  const desigs = dept.designations ?? [];
  if (!desigs.length) return '';
  const lowest =
    desigs.find((d) => d.is_lowest_level) ??
    [...desigs].sort((a, b) => b.rank - a.rank)[0];
  const level = (lowest?.territory_level ?? '').replace(/\s*Level$/i, '').trim();
  return level ? `Apex to ${level}` : '';
};

const STATUS_OPTS = ['All Status', 'Active', 'Inactive'];
const SORT_OPTS = ['Recently Updated', 'Name (A–Z)', 'Oldest First'];
const SORT_ORDERING: Record<string, string> = {
  'Recently Updated': '-updated_at',
  'Name (A–Z)': 'name',
  'Oldest First': 'updated_at',
};

const SELECT_CLS = 'dept-filter';

function StatusPill({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-[11px] py-[5px] text-xs font-medium text-[#059669]">
      <span className="size-1.5 rounded-full bg-[#10b981]" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line-soft bg-[#f3f4f6] px-[11px] py-[5px] text-xs font-medium text-[#4b5563]">
      <span className="size-1.5 rounded-full bg-[#9ca3af]" />
      Inactive
    </span>
  );
}

/** Shared grid template so header + body rows stay aligned. */
const GRID =
  'grid grid-cols-[minmax(240px,1.7fr)_minmax(190px,1.3fr)_150px_150px_130px_100px] items-center';

function DepartmentRow({
  dept,
  avatar,
  onEdit,
  onDelete,
}: {
  dept: GovtDepartment;
  avatar: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const levels = dept.levels ?? dept.designations?.length ?? 0;
  const span = spanLabel(dept);
  const active = dept.active ?? dept.status === 'Active';
  return (
    <div className={[GRID, 'border-t border-[#f3f4f6]'].join(' ')}>
      {/* Department name + avatar + summary */}
      <div className="flex items-center gap-3.5 px-6 py-4">
        <span
          className={[
            'flex size-10 shrink-0 items-center justify-center rounded-lg text-xs font-semibold',
            avatar,
          ].join(' ')}
        >
          {initialsOf(dept.name)}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-[13px] font-semibold leading-[16.25px] text-[#111827]">{dept.name}</p>
          {dept.description ? (
            <p className="line-clamp-2 text-xs leading-4 text-[#9ca3af]">{dept.description}</p>
          ) : null}
        </div>
      </div>

      {/* Territory hierarchy */}
      <div className="px-6 py-4">
        {dept.territory_hierarchy ? (
          <span className="inline-flex rounded-md bg-brand-shade px-2.5 py-1.5 text-xs leading-4 text-ink">
            {dept.territory_hierarchy}
          </span>
        ) : (
          <span className="text-xs text-[#9ca3af]">—</span>
        )}
      </div>

      {/* Ranks / designations */}
      <div className="px-6 py-4 text-xs leading-4">
        <span className="font-bold text-[#111827]">{levels} Levels </span>
        {span ? <span className="text-[#9ca3af]">({span})</span> : null}
      </div>

      {/* Status */}
      <div className="px-6 py-4">
        <StatusPill active={active} />
      </div>

      {/* Last updated */}
      <div className="px-6 py-4 text-xs text-[#6b7280]">{formatDate(dept.updated_at)}</div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-6 py-4">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${dept.name}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
        >
          <EditLineIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${dept.name}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}

export default function DepartmentListingPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [territory, setTerritory] = useState('All Hierarchies');
  const [status, setStatus] = useState(STATUS_OPTS[0]);
  const [sort, setSort] = useState(SORT_OPTS[0]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset to the first page whenever the query changes.
  useEffect(() => setPage(1), [search, territory, status, sort, pageSize]);

  const { data: filters } = useGetDepartmentFiltersQuery();
  const territoryOpts = ['All Hierarchies', ...(filters?.territory_hierarchies ?? [])];

  const { data, isFetching, isError } = useGetDepartmentsQuery({
    search: search.trim() || undefined,
    territory_hierarchy: territory === 'All Hierarchies' ? undefined : territory,
    status: status === 'All Status' ? undefined : status,
    ordering: SORT_ORDERING[sort],
    page,
    pageSize,
  });
  const [deleteDepartment, { isLoading: deleting }] = useDeleteDepartmentMutation();
  const [toDelete, setToDelete] = useState<GovtDepartment | null>(null);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const resetFilters = () => {
    setSearch('');
    setTerritory('All Hierarchies');
    setStatus(STATUS_OPTS[0]);
    setSort(SORT_OPTS[0]);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteDepartment(toDelete.id).unwrap();
      message.success('Department deleted.');
      setToDelete(null);
    } catch {
      message.error('Could not delete the department.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header + CTA */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold tracking-[-0.3px] text-[#2563eb]">
            Government Departments
          </span>
          <h1 className="text-2xl font-bold leading-8 text-[#111827]">Government Departments</h1>
          <p className="text-xs leading-4 text-[#6b7280]">
            Manage registered government departments, jurisdictional hierarchies, designations, and
            administrative officers.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/dept-configuration/new')}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-dark"
        >
          <PlusIcon className="size-4" />
          Create Department
        </button>
      </div>

      {/* Filter & search toolbar */}
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-[17px] shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[280px] flex-1">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search department by name or hierarchy..."
              className="w-full rounded-lg border border-[#e5e7eb] bg-white py-2.5 pl-10 pr-4 text-xs text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <Select
            className={SELECT_CLS}
            value={territory}
            onChange={setTerritory}
            options={territoryOpts.map((o) => ({ value: o, label: o }))}
            labelRender={(p) => `Territory: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <Select
            className={SELECT_CLS}
            value={status}
            onChange={setStatus}
            options={STATUS_OPTS.map((o) => ({ value: o, label: o }))}
            labelRender={(p) => `Status: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <Select
            className={SELECT_CLS}
            value={sort}
            onChange={setSort}
            options={SORT_OPTS.map((o) => ({ value: o, label: o }))}
            labelRender={(p) => `Sort: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <button
            type="button"
            onClick={resetFilters}
            aria-label="Refresh list"
            className="rounded-lg border border-[#e5e7eb] bg-white p-2.5 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
          >
            <RefreshIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header */}
            <div className={[GRID, 'bg-[#f9fafb]/50 border-b border-[#f3f4f6]'].join(' ')}>
              {(
                [
                  ['DEPARTMENT NAME', 'justify-start'],
                  ['TERRITORY HIERARCHY', 'justify-start'],
                  ['RANKS / DESIGNATIONS', 'justify-start'],
                  ['STATUS', 'justify-start'],
                  ['LAST UPDATED', 'justify-start'],
                  ['ACTIONS', 'justify-end text-right'],
                ] as const
              ).map(([label, align]) => (
                <div key={label} className={['flex px-6 py-[14px]', align].join(' ')}>
                  <span className="text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.55px] text-[#6b7280]">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Body */}
            {isError ? (
              <p className="px-6 py-12 text-center text-sm text-red-500">
                Could not load departments. Please try again.
              </p>
            ) : rows.length > 0 ? (
              rows.map((dept, i) => (
                <DepartmentRow
                  key={dept.id}
                  dept={dept}
                  avatar={AVATARS[i % AVATARS.length]}
                  onEdit={() => navigate(`/admin/dept-configuration/${dept.id}/edit`)}
                  onDelete={() => setToDelete(dept)}
                />
              ))
            ) : (
              <p className="px-6 py-12 text-center text-sm text-ink-secondary">
                {isFetching ? 'Loading departments…' : 'No departments match your filters.'}
              </p>
            )}
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="departments"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>

      <ConfirmDeleteModal
        open={toDelete !== null}
        title="Delete Department"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">{toDelete?.name}</span>? This action cannot be
            undone.
          </>
        }
        loading={deleting}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
